/**
 * A tiny path router.
 *
 * Paths only — the URL *fragment* is reserved for guided-arrival destinations
 * (`#dst=lat,lng,label`), which must never reach the server, so the router never
 * routes on it.
 */

export interface RouteContext {
  path: string;
  params: Record<string, string>;
  /** The raw fragment, minus the leading '#'. Read by guided-arrival, ignored by routing. */
  fragment: string;
}

/** A view mounts into the container and returns an optional teardown. */
export type View = (container: HTMLElement, context: RouteContext) => void | (() => void);

interface CompiledRoute {
  pattern: RegExp;
  keys: string[];
  view: View;
}

function compile(path: string): { pattern: RegExp; keys: string[] } {
  const keys: string[] = [];
  const source = path
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      if (!segment.startsWith(':')) return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      keys.push(segment.slice(1));
      return '([^/]+)';
    })
    .join('/');
  return { pattern: new RegExp(`^/${source}/?$`), keys };
}

export class Router {
  private readonly routes: CompiledRoute[] = [];
  private fallback: View | null = null;
  private teardown: (() => void) | null = null;

  constructor(private readonly container: HTMLElement) {}

  route(path: string, view: View): this {
    const { pattern, keys } = compile(path);
    this.routes.push({ pattern, keys, view });
    return this;
  }

  notFound(view: View): this {
    this.fallback = view;
    return this;
  }

  start(): this {
    window.addEventListener('popstate', () => this.resolve());
    this.resolve();
    return this;
  }

  /** Navigate without a full page load. */
  go(path: string, options: { replace?: boolean } = {}): void {
    if (options.replace) window.history.replaceState(null, '', path);
    else window.history.pushState(null, '', path);
    this.resolve();
  }

  private resolve(): void {
    const path = window.location.pathname;
    const fragment = window.location.hash.slice(1);

    this.teardown?.();
    this.teardown = null;

    for (const route of this.routes) {
      const match = route.pattern.exec(path);
      if (!match) continue;

      const params: Record<string, string> = {};
      route.keys.forEach((key, index) => {
        const value = match[index + 1];
        if (value !== undefined) params[key] = decodeURIComponent(value);
      });

      this.mount(route.view, { path, params, fragment });
      return;
    }

    if (this.fallback) this.mount(this.fallback, { path, params: {}, fragment });
  }

  private mount(view: View, context: RouteContext): void {
    this.container.replaceChildren();
    this.container.scrollTop = 0;
    this.teardown = view(this.container, context) ?? null;
  }
}
